<div class="b1-form p-6 bg-white rounded-lg shadow-md">
  <div class="flex justify-between items-center mb-6 pb-4 border-b border-slate-200">
    <h1 class="text-xl font-semibold text-slate-800">
      {{ recordId ? 'Edit Survey' : 'Add Survey' }}
    </h1>
    <button
      type="button"
      mat-stroked-button
      color="primary"
      (click)="goBack()"
    >
      ← {{ "BACK" | translate }}
    </button>
  </div>

  <form
    [formGroup]="surveyForm"
    (ngSubmit)="onSubmit()"
  >
  <div class="grid grid-cols-12 gap-6">
    <ng-container *ngFor="let field of commonFields">
      <div class="col-span-6" *ngIf="field.type == 'text'">
        <mat-label>{{ field.label }}</mat-label>
        <mat-form-field appearance="outline" class="w-full">
          <input matInput [formControlName]="field.key" [type]="field.type" />
        </mat-form-field>
      </div>
      <div class="col-span-6" *ngIf="field.type == 'select'">
        <mat-label>{{ field.label }}</mat-label>
        <mat-form-field appearance="outline" class="w-full">
          <mat-select
            [formControlName]="field.key"
            (selectionChange)="field.onChange?.($event.value)"
          >
            <mat-option *ngIf="field.key === 'projectId'" [value]="''">Select project</mat-option>
            <mat-option
              *ngFor="let option of optionsMap[field.optionKey]"
              [value]="option.value"
            >
              {{ option.label }}
            </mat-option>
          </mat-select>
          <mat-error *ngIf="surveyForm.get(field.key)?.hasError('required')">
            {{ field.label }} is required
          </mat-error>
        </mat-form-field>
        <p
          *ngIf="field.key === 'projectId' && !optionsMap['projectOptions']?.length"
          class="text-sm text-slate-500 -mt-3 mb-2"
        >
          No accessible projects
        </p>
      </div>
      <div class="col-span-6" *ngIf="field.type == 'textarea'">
        <mat-label>{{ field.label }}</mat-label>
        <mat-form-field appearance="outline" class="w-full">
          <textarea matInput [formControlName]="field.key" rows="1"></textarea>
        </mat-form-field>
      </div>
      <div class="col-span-6" *ngIf="field.type === 'date'">
        <mat-label>{{ field.label }}</mat-label>

        <mat-form-field appearance="outline" class="w-full">
          <input
            matInput
            [matDatepicker]="picker"
            [formControlName]="field.key"
          />
          <mat-datepicker-toggle
            matSuffix
            [for]="picker"
          ></mat-datepicker-toggle>
          <mat-datepicker #picker></mat-datepicker>
        </mat-form-field>
      </div>
    </ng-container>
    <ng-container *ngIf="domainFields[selectedDomain]">
      <ng-container *ngFor="let field of domainFields[selectedDomain]">
        <div class="col-span-6">
          <mat-label>{{ field.label }}</mat-label>
          <mat-form-field appearance="outline" class="w-full">
            <input matInput [formControlName]="field.key" [type]="field.type" />
          </mat-form-field>
        </div>
      </ng-container>
    </ng-container>
  </div>

  <div *ngIf="isRoadDomain" class="mt-4 p-4 border border-slate-200 rounded-lg bg-slate-50">
    <div class="flex items-center justify-between mb-3">
      <h3 class="text-sm font-semibold text-slate-700">LRS corridor (optional)</h3>
      <button mat-stroked-button type="button" (click)="openSurveyOnMap()">Open on map</button>
    </div>
    <div class="grid grid-cols-12 gap-4">
      <div class="col-span-6">
        <mat-label>Route</mat-label>
        <mat-form-field appearance="outline" class="w-full">
          <mat-select formControlName="lrsRouteId">
            <mat-option [value]="null">None</mat-option>
            <mat-option *ngFor="let route of lrsRoutes" [value]="route.id">
              {{ route.route_code }} — {{ route.route_name }}
            </mat-option>
          </mat-select>
        </mat-form-field>
      </div>
      <div class="col-span-3">
        <mat-label>From km</mat-label>
        <mat-form-field appearance="outline" class="w-full">
          <input matInput type="number" step="0.001" formControlName="lrsFromKm" />
        </mat-form-field>
      </div>
      <div class="col-span-3">
        <mat-label>To km</mat-label>
        <mat-form-field appearance="outline" class="w-full">
          <input matInput type="number" step="0.001" formControlName="lrsToKm" />
        </mat-form-field>
      </div>
      <div class="col-span-6">
        <mat-label>Chainage label</mat-label>
        <mat-form-field appearance="outline" class="w-full">
          <input matInput formControlName="lrsChainage" placeholder="e.g. NH-44 @ 45+200" />
        </mat-form-field>
      </div>
    </div>
  </div>

  <div class="mt-4 p-4 border border-slate-200 rounded-lg bg-slate-50">
    <h3 class="text-sm font-semibold text-slate-700 mb-3">Survey reports</h3>
    <input type="file" (change)="onUploadReport($event)" [disabled]="uploading" />
    <p *ngIf="uploading" class="text-xs text-slate-500 mt-2">Uploading…</p>
    <ul class="mt-3 space-y-1" *ngIf="documents.length">
      <li *ngFor="let doc of documents; let i = index" class="flex items-center justify-between text-sm">
        <a [href]="doc.url" target="_blank" rel="noopener" class="text-blue-600 hover:underline">{{ doc.name }}</a>
        <button type="button" mat-button color="warn" (click)="removeDocument(i)">Remove</button>
      </li>
    </ul>
  </div>

  <div class="mt-4 p-4 border border-slate-200 rounded-lg">
    <h3 class="text-sm font-semibold text-slate-700 mb-3">
      Surveyed quantities vs BOQ{{ boqCode ? ' (' + boqCode + ')' : '' }}
    </h3>
    <p *ngIf="noBoq" class="text-sm text-slate-500">No BOQ to version</p>
    <table *ngIf="boqLines.length" class="w-full text-sm">
      <thead>
        <tr class="text-left text-slate-500">
          <th class="py-2">Item</th>
          <th class="py-2">Unit</th>
          <th class="py-2">Estimated qty</th>
          <th class="py-2">Surveyed qty</th>
        </tr>
      </thead>
      <tbody>
        <tr *ngFor="let line of boqLines" class="border-t border-slate-100">
          <td class="py-2">
            <div class="font-medium">{{ line.item_code || ('#' + line.item_id) }}</div>
            <div class="text-slate-500">{{ line.item_description }}</div>
          </td>
          <td class="py-2">{{ line.unit || '—' }}</td>
          <td class="py-2">{{ line.estimated_qty }}</td>
          <td class="py-2">
            <input
              class="w-24 border border-slate-300 rounded px-2 py-1"
              type="number"
              step="0.01"
              [(ngModel)]="line.surveyed_qty"
              [ngModelOptions]="{standalone: true}"
            />
          </td>
        </tr>
      </tbody>
    </table>
  </div>

  <div class="mt-4 p-4 border border-emerald-200 rounded-lg bg-emerald-50" *ngIf="boqComparison.length">
    <h3 class="text-sm font-semibold text-emerald-800 mb-3">Last approved BOQ comparison</h3>
    <table class="w-full text-sm">
      <thead>
        <tr class="text-left text-emerald-700">
          <th class="py-2">Item</th>
          <th class="py-2">Estimated</th>
          <th class="py-2">Surveyed</th>
          <th class="py-2">Delta</th>
        </tr>
      </thead>
      <tbody>
        <tr *ngFor="let line of boqComparison" class="border-t border-emerald-100">
          <td class="py-2">{{ line.item_code || ('#' + line.item_id) }} — {{ line.item_description }}</td>
          <td class="py-2">{{ line.estimated_qty }}</td>
          <td class="py-2">{{ line.surveyed_qty }}</td>
          <td class="py-2">{{ (line.surveyed_qty || 0) - (line.estimated_qty || 0) }}</td>
        </tr>
      </tbody>
    </table>
  </div>

  <div class="mt-6 flex gap-4">
    <button mat-raised-button color="primary" type="submit" [disabled]="saving">
      {{ recordId ? 'Update' : 'Submit' }}
    </button>
    <button mat-button type="button" (click)="onReset()">Reset</button>
  </div>
  </form>
</div>

